import {
  InAppContentService,
  NewsEntry,
  CreateNewsEntryRequest,
  UpdateNewsEntryRequest,
} from '@beeboard/common';
import { defineStore } from 'pinia';

type NewsStoreState = {
  // Rocket popover — published entries visible to the current user
  newsEntries: NewsEntry[];
  unreadCount: number;
  isLoaded: boolean;
  // Admin page — all entries regardless of targeting, drafts included
  adminNewsEntries: NewsEntry[];
};

export const useNewsStore = defineStore('newsStore', {
  state: (): NewsStoreState => ({
    newsEntries: [],
    unreadCount: 0,
    isLoaded: false,
    adminNewsEntries: [],
  }),

  actions: {
    async loadNewsEntries() {
      const result = await InAppContentService.getNewsEntries();
      this.newsEntries = result.items;
      this.unreadCount = result.unread_count;
      this.isLoaded = true;
    },

    async loadAdminNewsEntries() {
      const result = await InAppContentService.getNewsEntries(true);
      this.adminNewsEntries = result.items;
    },

    async recordView(newsId: string) {
      await InAppContentService.recordNewsEntryView(newsId);
      const entry = this.newsEntries.find((e) => e.id === newsId);
      if (entry && !entry.is_viewed) {
        entry.is_viewed = true;
        this.unreadCount = Math.max(0, this.unreadCount - 1);
      }
    },

    async createNewsEntry(data: CreateNewsEntryRequest) {
      const entry = await InAppContentService.createNewsEntry(data);
      this.adminNewsEntries.unshift(entry);
      // Rocket list is refetched on popover open; no sync needed here.

      return entry;
    },

    async updateNewsEntry(newsId: string, data: UpdateNewsEntryRequest) {
      const entry = await InAppContentService.updateNewsEntry(newsId, data);
      const adminIndex = this.adminNewsEntries.findIndex(
        (e) => e.id === newsId,
      );
      if (adminIndex !== -1) {
        this.adminNewsEntries[adminIndex] = entry;
      }
      const index = this.newsEntries.findIndex((e) => e.id === newsId);
      if (index !== -1) {
        this.newsEntries[index] = entry;
      }

      return entry;
    },

    async deleteNewsEntry(newsId: string) {
      await InAppContentService.deleteNewsEntry(newsId);
      this.adminNewsEntries = this.adminNewsEntries.filter(
        (e) => e.id !== newsId,
      );
      this.newsEntries = this.newsEntries.filter((e) => e.id !== newsId);
    },
  },
});
